import React, { useCallback, useMemo, useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import moment from "../utils/moment";

interface TimezoneProps {
  label?: React.ReactNode;
  name?: string;
  value?: string;
  onChange?: (e: any) => void;
  showBorder?: boolean;
  margin?: string;
}

interface TimezoneOption {
  value: string;
  label: string;
  offset: number;
}

const Timezone: React.FC<TimezoneProps> = ({
  label,
  name,
  value,
  onChange,
  showBorder = true,
  margin,
}) => {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const options = useMemo<TimezoneOption[]>(() => {
    // Only include canonical IANA zones in Continent/City format.
    // Excludes legacy aliases (EST, US/Central, Etc/GMT+5, etc.) that
    // cause confusing duplicate offsets in the dropdown.
    const validPrefixes = [
      "Africa/",
      "America/",
      "Antarctica/",
      "Arctic/",
      "Asia/",
      "Atlantic/",
      "Australia/",
      "Europe/",
      "Indian/",
      "Pacific/",
    ];
    return moment.tz
      .names()
      .filter((tz) => validPrefixes.some((p) => tz.startsWith(p)))
      .map((tz) => {
        const offset = moment.tz(tz).utcOffset();
        const formatted = moment.tz(tz).format("Z");
        const cityName = tz.split("/").slice(1).join("/").replace(/_/g, " ");
        return {
          value: tz,
          label: `(UTC${formatted}) ${cityName} — ${tz}`,
          offset,
        };
      })
      .sort((a, b) => a.offset - b.offset);
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const selectedLabel = useMemo(() => {
    if (!value) return "";
    const match = options.find((o) => o.value === value);
    return match?.label || value;
  }, [value, options]);

  const handleSelect = useCallback(
    (tz: string) => {
      if (onChange) {
        onChange({ target: { name, value: tz } });
      }
      setSearch("");
      setIsOpen(false);
    },
    [name, onChange]
  );

  const handleBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    // Close the dropdown only if focus left the entire container.
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setIsOpen(false);
      setSearch("");
    }
  }, []);

  return (
    <Form.Group className={margin === "mb-0" ? "mb-0" : "mb-3"}>
      {label && <Form.Label>{label}</Form.Label>}
      <div ref={containerRef} style={{ position: "relative" }} onBlur={handleBlur}>
        <input
          type="text"
          className={`form-control ${showBorder ? "selectBorder" : "border-0"}`}
          placeholder={value ? selectedLabel : "Search timezone..."}
          value={isOpen ? search : value ? selectedLabel : ""}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          autoComplete="off"
          style={{ fontFamily: "Work Sans" }}
        />
        {isOpen && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              maxHeight: 250,
              overflowY: "auto",
              backgroundColor: "var(--surface-card)",
              border: "1px solid var(--border-soft)",
              borderRadius: 6,
              // Sit above the .formStickyBar action card on the create
              // listing page (z-index: 10) and other in-page chrome,
              // while staying below modals/toasts.
              zIndex: 1000,
              boxShadow: "0 4px 12px rgba(0,0,0,.1)",
              fontFamily: "Work Sans",
            }}
          >
            {filtered.length === 0 && (
              <div
                style={{
                  padding: "10px 14px",
                  color: "var(--text-muted)",
                  fontSize: 14,
                }}
              >
                No timezone found.
              </div>
            )}
            {filtered.map((option) => (
              <div
                key={option.value}
                tabIndex={0}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(option.value)}
                style={{
                  padding: "8px 14px",
                  cursor: "pointer",
                  fontSize: 14,
                  backgroundColor:
                    option.value === value
                      ? "var(--surface-cream)"
                      : "transparent",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    "var(--surface-cream-soft)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    option.value === value
                      ? "var(--surface-cream)"
                      : "transparent")
                }
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </Form.Group>
  );
};

export default Timezone;
